import dayjs from 'dayjs'
import { createContext, useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { GetTotalProduct, GetTotalStaff } from '~/api'
import locale from '~/locale'
import { RootState } from '~/redux/store'
import { BusinessI } from '~/types'
import { $log } from '~/utils/logger'
import { useAuth } from '../Auth/AuthProvider'
import { SubscriptionPaywall } from './SubscriptionPaywall'

interface SubscriptionProviderProps {
    children: JSX.Element | JSX.Element[]
}

type SubscriptionPlanT = 'free' | 'advance'

interface SubscriptionContextI {
    execFeature: (callback?: () => void) => boolean
    execFeatureByTier: (expectedTier: keyof typeof limitMap, callback?: () => void) => boolean
    execFeatureLimitCount: (feature: 'products' | 'staff', callback?: () => void) => Promise<boolean>
    execFeatureLimitRange: (
        feature: 'history' | 'report' | 'stock_report' | 'customer_report',
        dateStart: string,
        callback?: () => void
    ) => boolean
    getSubscription: () => [boolean, SubscriptionPlanT, BusinessI['subscription']]
    isFreeTimeLimitExceeded: (numDays: number) => boolean
    openPaywall: (businessId?: number) => void
}

const SubscriptionContext = createContext<SubscriptionContextI>({
    execFeature: () => false,
    execFeatureByTier: () => false,
    execFeatureLimitCount: () => Promise.resolve(false),
    execFeatureLimitRange: () => false,
    getSubscription: () => [false, 'free', undefined],
    isFreeTimeLimitExceeded: () => false,
    openPaywall: () => { }
})

export const useSubscription = () => useContext(SubscriptionContext)

const limitMap = {
    free: {
        tier: 0,
        products: 20,
        staff: 1,
        history: 1,
        report: 7,
        stock_report: 7,
        customer_report: 7
    },
    advance: {
        tier: 3,
        products: 100000,
        staff: 25,
        history: 9999,
        report: 9999,
        stock_report: 9999,
        customer_report: 9999
    }
}

export const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
    const language = useSelector((state: RootState) => state.language.language)
    // Assuming locale has a specific type, e.g. LocaleType
    const localeText = (locale[language as keyof typeof locale] || locale.en) as any
    const { currentBusiness, businesses } = useAuth()
    const [showPaywall, setShowPaywall] = useState(false)
    const [selectedBusinessId, setSelectedBusinessId] = useState<number | undefined>()

    const getSubscription = (): [boolean, SubscriptionPlanT, BusinessI['subscription']] => {
        const business = currentBusiness()

        if (!business) {
            return [false, 'free', undefined]
        }

        const subscription = business.subscription

        if (!subscription || dayjs().isAfter(dayjs(subscription.endDate).local())) {
            return [false, 'free', subscription]
        }

        return [true, 'advance', subscription]
    }


    const execFeature = (callback: () => void = () => false) => {
        const [isSubscribed] = getSubscription()

        if (!isSubscribed) {
            openPaywall()
            return false
        }

        callback()
        return true
    }

    const execFeatureByTier = (expectedTier: keyof typeof limitMap, callback: () => void = () => false) => {
        const [isSubscribed, plan] = getSubscription()

        if (!isSubscribed || limitMap[plan].tier < limitMap[expectedTier].tier) {
            openPaywall()
            return false
        }

        callback()
        return true
    }

    const execFeatureLimitCount = async (feature: 'products' | 'staff', callback: () => void = () => false) => {
        const bussinessId = currentBusiness()?.businessId || ''
        const count =
            feature === 'products'
                ? await GetTotalProduct(Number(bussinessId), localeText).then((res) => res.data || 0)
                : await GetTotalStaff(Number(bussinessId), localeText).then((res) => res.data || 0)

        const [_, plan] = getSubscription()

        if (count >= limitMap[plan][feature]) {
            openPaywall()
            return false
        }

        callback()
        return true
    }

    const execFeatureLimitRange = (
        feature: 'history' | 'report' | 'stock_report' | 'customer_report',
        dateStart: string,
        callback: () => void = () => false
    ) => {
        const dateDiff = dayjs().diff(dayjs(dateStart), 'day')
        const [_, plan] = getSubscription()

        if (dateDiff > limitMap[plan][feature]) {
            openPaywall()
            return false
        }

        callback()
        return true
    }

    const isFreeTimeLimitExceeded = (numDays: number) => {
        const [isSubscribed] = getSubscription()

        if (isSubscribed) {
            return false
        }

        const dateBusinessCreated = dayjs(currentBusiness()?.createdAt).local()
        const dateLimit = dateBusinessCreated.add(numDays, 'day')

        return dateLimit.isBefore(dayjs())
    }

    const openPaywall = (businessId?: number) => {
        const id = businessId || Number(currentBusiness()?.businessId)
        setSelectedBusinessId(id)
        try {
            AppChannel.postMessage('showPaywall')
            $log('Open Paywall', 'Post Message')
        } catch (e) {
            setShowPaywall(true)
            $log('Open Paywall', 'Default')
        }
    }

    const value = {
        execFeature,
        execFeatureByTier,
        execFeatureLimitCount,
        execFeatureLimitRange,
        getSubscription,
        isFreeTimeLimitExceeded,
        openPaywall
    }

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
            <SubscriptionPaywall
                businessId={selectedBusinessId}
                show={showPaywall}
                onClose={() => setShowPaywall(false)}></SubscriptionPaywall>
        </SubscriptionContext.Provider>
    )
}
